/**
 * Canonical DOM predicates on the "editability" of an element.
 *
 * PATTERN TO FOLLOW: the question "is this element editable?" is answered ONLY
 * from here. Previously this logic (`TAGS.has(tagName) || isContentEditable`)
 * was duplicated in `lib/gestures/guards`, `lib/interaction/context`,
 * `components/ui/dialog` and `todo-detail.helpers` with four slightly
 * different variants, risking divergence. Import these helpers instead of rewriting
 * the check.
 *
 * There are two predicates with DISTINCT semantics — choose according to intent:
 *  - `isEditableElement`: any form control (includes SELECT,
 *    checkbox…) or `contenteditable`. Use it to *suppress* global
 *    gestures/shortcuts when focus is inside a control.
 *  - `isTextEntryElement`: ONLY free text input (textarea or textual-type
 *    input). Stricter. Use it for decisions based on the text
 *    cursor, e.g. not stealing arrows while editing text.
 *
 * Note: there are specific checks (e.g. in detail key handling) that
 * purposely use their own tag sets (exclude SELECT, include BUTTON/A…)
 * because they ask something different ("is this key consumed by the control?"). Those
 * MUST NOT be collapsed here: the distinct semantics are intentional.
 */

/** Form controls that count as "editable". */
const FORM_CONTROL_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/**
 * `true` if `el` is a form control (input/textarea/select) or a
 * `contenteditable` element.
 */
export function isEditableElement(el: Element | null): el is HTMLElement {
  return el instanceof HTMLElement && (FORM_CONTROL_TAGS.has(el.tagName) || el.isContentEditable);
}

/**
 * `true` if there is any editable field focused, or any `contenteditable`
 * exists in the document. Intended for global gates (dialog dragging,
 * navigation swipe) that must yield to editing. SSR-safe.
 */
export function isEditingActive(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  if (document.querySelector('[contenteditable="true"], [contenteditable=""]') !== null) {
    return true;
  }
  return isEditableElement(document.activeElement);
}
